import { useCallback, useEffect, useRef, useState } from 'react';
import { useFeatureFlags } from 'shared/providers/FeatureFlags/useFeatureFlags';
import {
  checkDonorName,
  DonorNameRange,
  isMeaningfulProgramName,
} from './api';

export const DONOR_NAME_DEBOUNCE_MS = 500;

interface CheckResult {
  text: string;
  ranges: DonorNameRange[];
}

const NO_RANGES: DonorNameRange[] = [];

export interface ProgramNameDonorFlag {
  enabled: boolean;
  isChecking: boolean;
  ranges: DonorNameRange[];
  detected: boolean;
  kept: boolean;
  needsDecision: boolean;
  modalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  keepName: () => void;
  flushCheck: () => Promise<boolean>;
}

export const useProgramNameDonorFlag = (
  name: string,
  alreadyKeptName?: string | null,
): ProgramNameDonorFlag => {
  const enabled = useFeatureFlags('levelOneFeedback');
  const [result, setResult] = useState<CheckResult | null>(null);
  const [keptName, setKeptName] = useState<string | null>(
    alreadyKeptName ?? null,
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const nameRef = useRef(name);
  nameRef.current = name;

  useEffect(() => {
    if (!enabled || !isMeaningfulProgramName(name)) {
      setResult(null);
      setIsChecking(false);
      return undefined;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setIsChecking(true);
      try {
        const response = await checkDonorName(name, controller.signal);
        if (!controller.signal.aborted) {
          setResult({ text: name, ranges: response.matches });
        }
      } catch {
        if (!controller.signal.aborted) setResult(null);
      } finally {
        if (!controller.signal.aborted) setIsChecking(false);
      }
    }, DONOR_NAME_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
      setIsChecking(false);
    };
  }, [name, enabled]);

  const ranges =
    result && result.text === name && result.ranges.length > 0
      ? result.ranges
      : NO_RANGES;
  const detected = ranges.length > 0;
  const kept = detected && keptName === name;

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);
  const keepName = useCallback(() => {
    setKeptName(nameRef.current);
    setModalOpen(false);
  }, []);

  // Resolves true when the current name still needs the user's decision.
  const flushCheck = useCallback(async () => {
    if (!enabled || !isMeaningfulProgramName(name)) return false;
    if (result && result.text === name) {
      return result.ranges.length > 0 && keptName !== name;
    }
    try {
      const response = await checkDonorName(name);
      if (nameRef.current === name) {
        setResult({ text: name, ranges: response.matches });
      }
      return response.matches.length > 0 && keptName !== name;
    } catch {
      return false;
    }
  }, [enabled, name, result, keptName]);

  return {
    enabled,
    isChecking,
    ranges,
    detected,
    kept,
    needsDecision: detected && !kept,
    modalOpen,
    openModal,
    closeModal,
    keepName,
    flushCheck,
  };
};
